import moment from 'moment';

type NumMapType = Record<'billion' | 'million' | 'thousand', { value: number; symbol: string }>;

const NumMap: NumMapType = {
    billion: {
        value: 1000000000,
        symbol: 'B',
    },
    million: {
        value: 1000000,
        symbol: 'M',
    },
    thousand: {
        value: 1000,
        symbol: 'K',
    },
} as const;

const isBillions = (num: number) => num >= NumMap.billion.value;
const isMillions = (num: number) => num >= NumMap.million.value;
const isThousands = (num: number) => num >= NumMap.thousand.value;

const intlFormat = (num: number) => new Intl.NumberFormat().format(Math.round(num * 10) / 10);

export const needsFormatting = (num: number) => isThousands(num);

export const countFormatter = (num: number) => {
    if (isBillions(num)) return `${intlFormat(num / NumMap.billion.value)}${NumMap.billion.symbol}`;
    if (isMillions(num)) return `${intlFormat(num / NumMap.million.value)}${NumMap.million.symbol}`;
    if (isThousands(num)) return `${intlFormat(num / NumMap.thousand.value)}${NumMap.thousand.symbol}`;
    return intlFormat(num);
};

/**
 * Format time in ms to a human readable string
 * @param timeFormat - The format of the time to return
 * @returns A function that formats a time value to a human readable string
 */
export function getDurationFromTime(
    timeFormat: 'years' | 'months' | 'weeks' | 'days' | 'hours' | 'minutes' | 'seconds' | 'milliseconds' | 'ms' = 'ms',
) {
    return (timeVal: number | null | undefined): string => {
        if (timeVal === null || timeVal === undefined) return '-';

        const durationObj = moment.duration(timeVal, timeFormat);
        let duration = '';

        const timeUnits = [
            { value: Math.floor(durationObj.asYears()), label: 'year' },
            { value: Math.floor(durationObj.asMonths() % 12), label: 'month' },
            { value: Math.floor(durationObj.asDays() % 30), label: 'day' },
            { value: durationObj.hours(), label: 'hr' },
            { value: durationObj.minutes(), label: 'min' },
            { value: durationObj.seconds(), label: 'sec' },
        ];

        timeUnits.forEach((tUnit) => {
            if (tUnit.value > 0) {
                const plural = tUnit.value === 1 ? '' : 's';
                duration += `${tUnit.value} ${tUnit.label}${plural} `;
            }
        });

        if (!duration.trim()) {
            duration = '0 sec';
        }

        return duration.trim();
    };
}
